import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ShieldCheck, BookOpen, FileCheck2, Briefcase, Award, Sparkles, ArrowRight, CheckCircle2, XCircle } from "lucide-react";

export default function Landing() {
  const { devLogin, user } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [loadingLogin, setLoadingLogin] = useState(false);

  useEffect(() => {
    api.get("/config/public").then(({ data }) => setConfig(data));
  }, []);

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/dashboard");
  }, [user, nav]);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const quickStart = async (kind) => {
    setLoadingLogin(true);
    try {
      const map = {
        free: { email: "free@example.com", name: "Free User", premium: false },
        premium: { email: "premium@example.com", name: "Premium User", premium: true },
        admin: { email: "admin@hsecareerhub.com", name: "Admin", premium: false },
      }[kind];
      await devLogin(map.email, map.name, map.premium);
      nav(kind === "admin" ? "/admin" : "/dashboard");
    } finally { setLoadingLogin(false); }
  };

  const highlights = [
    { icon: BookOpen, title: "HSE Digital Library", desc: "Original notes across construction, environmental & general HSE." },
    { icon: FileCheck2, title: "Mock Exam Engine", desc: "Timed, scored practice for NEBOSH, NVQ, OTHM & Qualifi." },
    { icon: Briefcase, title: "HSE Job Search", desc: "UAE, KSA, Qatar, Oman and beyond — save & track applications." },
    { icon: Award, title: "Certifications Prep", desc: "NEBOSH IGC, NVQ 5/6/7, OTHM 5/6/7, Qualifi Diploma." },
  ];

  const compare = [
    ["Limited HSE articles", "Full HSE library"],
    ["10 mock questions", "Unlimited mock exams"],
    ["5 interview questions", "Full interview bank + simulator"],
    ["3 templates", "All templates & checklists"],
    ["Basic CV builder", "Premium CV templates + PDF export"],
    ["Community daily tip", "Premium daily briefings"],
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-900 dark:bg-amber-500 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-amber-400 dark:text-slate-950" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-base tracking-tight">HSE Career Hub</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Learn · Certify · Get Hired</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="landing-google-signin" onClick={startGoogle} className="btn-outline !py-1.5 !px-3 text-xs">Sign in with Google</button>
            <button data-testid="landing-start-free-btn" onClick={() => quickStart("free")} disabled={loadingLogin} className="btn-primary !py-1.5 !px-3 text-xs">Start Free</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="pill bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300 mb-6">
              <Sparkles className="h-3 w-3" /> Built for HSE professionals
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Everything you need to build your <span className="text-amber-500">HSE career.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
              Learn HSE. Prepare for certifications. Practice mock exams. Prepare for interviews. Find HSE jobs — all in one professional workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-start-free-btn" onClick={() => quickStart("free")} className="btn-primary">
                Start Free <ArrowRight className="h-4 w-4" />
              </button>
              <button data-testid="hero-explore-premium-btn" onClick={() => quickStart("premium")} className="btn-accent">
                Explore Premium
              </button>
              <button data-testid="hero-admin-demo-btn" onClick={() => quickStart("admin")} className="btn-outline">
                Try Admin
              </button>
            </div>
            <div className="mt-4 text-xs text-slate-500 dark:text-slate-400">7-day Premium trial included · No credit card required</div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-lg">
              <img alt="HSE safety officer on site" src="https://images.unsplash.com/photo-1688841747582-41097036109d?crop=entropy&cs=srgb&fm=jpg&q=85" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Live mock exam</div>
                    <div className="text-sm font-semibold">NEBOSH IGC · 15 questions</div>
                  </div>
                  <div className="mono text-amber-500 text-2xl font-bold">89%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-7xl px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {highlights.map((h, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 card-hover">
              <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-3">
                <h.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">{h.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-7xl px-4 md:px-8 py-10">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Free vs Premium</h2>
        <p className="mt-2 text-slate-600 dark:text-slate-400">Upgrade unlocks the full HSE library, unlimited exams and interview simulator.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-slate-900">
            <div className="text-xs uppercase tracking-widest text-slate-500">Free</div>
            <div className="text-3xl font-extrabold mt-1">$0</div>
            <ul className="mt-4 space-y-2">
              {compare.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-slate-400 mt-0.5" /> {c[0]}
                </li>
              ))}
            </ul>
            <button data-testid="cta-start-free-btn" onClick={() => quickStart("free")} className="btn-outline mt-6 w-full">Start Free</button>
          </div>
          <div className="rounded-2xl border-2 border-amber-500 p-6 bg-white dark:bg-slate-900 relative">
            <div className="pill bg-amber-500 text-slate-950 absolute -top-3 left-6">Recommended</div>
            <div className="text-xs uppercase tracking-widest text-amber-600">Premium</div>
            <div className="text-3xl font-extrabold mt-1">{config?.plans?.find(p => p.plan_id === "annual")?.price ? `$${config.plans.find(p => p.plan_id === "annual").price}/yr` : "$79.99/yr"}</div>
            <ul className="mt-4 space-y-2">
              {compare.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> <span className="font-medium">{c[1]}</span>
                </li>
              ))}
            </ul>
            <button data-testid="cta-explore-premium-btn" onClick={() => quickStart("premium")} className="btn-accent mt-6 w-full">Try Premium Free</button>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-4 md:px-8 py-10 text-center text-xs text-slate-500 dark:text-slate-400">
        © HSE Career Hub · Independent HSE career platform. Original educational content — not affiliated with NEBOSH, OTHM, Qualifi or ISO.
      </footer>
    </div>
  );
}
